import { outstandingOf } from '@progear/finance-core';
import { docCell, docTypeBadgeCls } from '@/lib/ledgerCells';
import type { CashFlowEntry, RelatedDocument } from '@/lib/cashFlow';

/**
 * Pure presentation model for the "Seotud dokumendid" panel — the prepayment-offset
 * linkage (ETT ettemaksuarve ↔ ARV lõpparve). Like `ledgerCells.ts` this owns ZERO
 * money maths (L9): every euro is a stored fact on the link (its offset capacity) or
 * on the related document (its gross / settled cash), and is only ever *formatted* by
 * the view. The one resolved offset (which euro actually nets) is finance-core's
 * (`prepaymentOffsetAmountOf` / `effectiveCashFlowMoneyOf`), never recomputed here.
 * The panel renders identically in EVERY lens (enriched detail + calendar/register
 * hover) off this one model (L14), so a linked document never differs by lens.
 */

/** The relationship caption from THIS entry's perspective, keyed by relation kind then
 *  direction (L14 — one model, every lens):
 *  - `prepayment_offset` / `applied_from` → a prepayment (ETT) COVERS this entry (ARV final).
 *  - `prepayment_offset` / `applies_to`   → this entry (an ETT) COVERS the related ARV final.
 *  - `credit_note` / `applied_from` → this entry (an ARV) has been CREDITED by the related
 *    kreeditarve (the credit note sits in the link's prepayment slot → applied_from here).
 *  - `credit_note` / `applies_to`   → this entry (a kreeditarve) CREDITS the related ARV. */
export const RELATION_CAPTION: Record<
  RelatedDocument['relationKind'],
  Record<RelatedDocument['direction'], string>
> = {
  prepayment_offset: {
    applied_from: 'Kaetud ettemaksuga',
    applies_to: 'Katab lõpparvet',
  },
  credit_note: {
    applied_from: 'Krediteeritud kreeditarvega',
    applies_to: 'Krediteerib arvet',
  },
};

/** The caption for ONE related document — its kind + direction (see {@link RELATION_CAPTION}). */
export function relationCaption(doc: RelatedDocument): string {
  return RELATION_CAPTION[doc.relationKind][doc.direction];
}

export interface RelatedDocCell {
  /** The link row id (`cash_flow_entry_link.id`) — the unlink handle. */
  linkId: string;
  /** The OTHER document's entry id — open its detail from any lens. */
  relatedEntryId: string;
  /** Relationship caption (`RELATION_CAPTION`) from this entry's perspective. */
  caption: string;
  /** Abbreviated docType code badge (ARV/ETT/…), or null when the related doc has none. */
  code: string | null;
  /** Full Estonian docType label, or null. */
  label: string | null;
  /** Accent class for the code badge (token colours only — reuses the register ramp). */
  badgeCls: string;
  docNumber: string | null;
  party: string;
  /** The link's offset capacity, integer cents — a stored fact (display only, L9). */
  amountCents: number;
  /** How much of the related doc is itself settled in cash — the offset GATE. */
  settledCents: number;
  /** The related doc's gross, integer cents — a stored fact. */
  grossCents: number;
  expectedDate: string;
}

/** Map ONE related document to its display cell (identity + stored cents — no maths). */
export function relatedDocCell(doc: RelatedDocument): RelatedDocCell {
  const { code, label } = docCell(doc.relatedDocType);
  return {
    linkId: doc.linkId,
    relatedEntryId: doc.relatedEntryId,
    caption: relationCaption(doc),
    code,
    label,
    badgeCls: docTypeBadgeCls(doc.relatedDocType),
    docNumber: doc.relatedDocNumber,
    party: doc.relatedParty,
    amountCents: doc.amountCents,
    settledCents: doc.relatedSettledCents,
    grossCents: doc.relatedGrossCents,
    expectedDate: doc.relatedExpectedDate,
  };
}

/** Map an entry's related documents to display cells (the panel's whole input). */
export function relatedDocCells(entry: CashFlowEntry): RelatedDocCell[] {
  return entry.relatedDocuments.map(relatedDocCell);
}

/** The inline "⇄" jump's label in the register: the RELATED document's identity —
 *  its docType code + number (e.g. "ETT 104215") — so the linked prepayment/final is
 *  identifiable at a glance without opening it. Pure identity join, zero money maths
 *  (L9); reuses `docCell` so the code never reinvents the terminology. */
export function relationLinkLabel(doc: RelatedDocument): string {
  const { code } = docCell(doc.relatedDocType);
  return [code, doc.relatedDocNumber].filter(Boolean).join(' ');
}

/**
 * The prepayments (ETT) a final invoice (ARV) can still be linked to: same party +
 * same direction (kind) prepayment documents from the current window, excluding the
 * final itself and any prepayment ALREADY linked to it (no duplicate link → no
 * server 400). Pure equality filtering — no money maths; the link RPC remains the
 * authority on true remaining capacity (sum links ≤ each side gross).
 */
export function prepaymentCandidatesFor(
  final: CashFlowEntry,
  entries: CashFlowEntry[],
): CashFlowEntry[] {
  const linked = new Set(final.relatedDocuments.map((d) => d.relatedEntryId));
  return entries.filter(
    (e) =>
      e.docType === 'prepayment' &&
      e.kind === final.kind &&
      e.party === final.party &&
      e.id !== final.id &&
      !linked.has(e.id),
  );
}

/**
 * The offset to PROPOSE when linking a prepayment to a final: the smaller of the
 * final's still-outstanding cash (finance-core `outstandingOf` — the one truth, L9)
 * and the prepayment's gross. `Math.min` only SELECTS between two existing cent
 * figures (no VAT/sum/margin arithmetic); the link RPC clamps/validates the true
 * remaining capacity on both sides, so this is a sensible default, never the gate.
 */
export function proposedPrepaymentLinkCents(final: CashFlowEntry, prepayment: CashFlowEntry): number {
  return Math.min(outstandingOf(final), prepayment.money.gross);
}
